/**
 * Production-safe Logger Utility
 *
 * Creates prefixed loggers that suppress debug/info output in production builds.
 * - `debug` and `info` are no-ops when `import.meta.env.PROD` is true.
 * - `warn` and `error` always emit (via console.warn / console.error).
 *
 * Usage:
 *   import { createLogger } from '@/utils/logger';
 *   const logger = createLogger('ConversationPanel');
 *   logger.debug('Starting stream', { id: 123 });
 *   // → [ConversationPanel] Starting stream { id: 123 }   (dev only)
 */

export interface Logger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export function createLogger(prefix: string): Logger {
  const tag = `[${prefix}]`;

  if (import.meta.env.PROD) {
    return {
      debug: noop,
      info: noop,
      warn: (...args: unknown[]) => console.warn(tag, ...args),
      error: (...args: unknown[]) => console.error(tag, ...args),
    };
  }

  return {
    debug: (...args: unknown[]) => console.log(tag, ...args),
    info: (...args: unknown[]) => console.log(tag, ...args),
    warn: (...args: unknown[]) => console.warn(tag, ...args),
    error: (...args: unknown[]) => console.error(tag, ...args),
  };
}
